import { Box, ButtonBase, Card, CardActions, CardContent, CardHeader, SxProps, Typography } from '@mui/material'
import React from 'react'
import { getReadableAnimalFieldLabel, getReadableAnimalHairLength } from '../../../../domain/Animal/AnimalDisplay'
import { AnimalDto } from '../../../../interactors/gen/backendClient'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import { useEditAnimalMutation } from '../../../../store/useEditAnimalMutation'
import { useFormExtended } from '../../../../utils/hooks/useFormExtended'
import { ControlledChipInput } from '../../../common/ControlledChipInput'
import { ControlledMultipleFreeSoloAutoCompleteWithCategories } from '../../../common/ControlledMultipleFreeSoloAutocompleteWithCategories'
import { ReadOnlyChip } from '../../../common/ReadOnlyChip'
import { colors, spacingItem } from '../../../theme'
import { AnimalCardSaveButton } from '../AnimalCardSaveButton'
import { EditorPromptWithFields } from '../EditorPromptWithFields'

interface Props {
  animal: AnimalDto
  sx?: SxProps
}

type FormParams = {
  characteristics: Array<string>
  identifyingMarks: Array<string>
}

const DESCRIPTION_PREVIEW_LENGTH = 184

const getPlainTextFromHtml = (value: string | undefined): string => {
  if (!value) return ''

  if (typeof window !== 'undefined') {
    const parser = new DOMParser()
    const document = parser.parseFromString(value, 'text/html')
    return (document.body.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const AnimalAboutCard: React.FC<Props> = ({ animal }) => {
  const editAnimalMutation = useEditAnimalMutation({})
  const canEditAnimal = useCanEditAnimal()

  const getDefaultValues = (animal: AnimalDto): FormParams => ({
    characteristics: animal?.characteristics || [],
    identifyingMarks: animal?.identifyingMarks || [],
  })

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useFormExtended(animal, getDefaultValues)

  const onSubmit = async (data: FormParams) => {
    await editAnimalMutation.mutate({
      ...animal,
      characteristics: data.characteristics,
      identifyingMarks: data.identifyingMarks,
    })
  }

  const options = [
    {
      title: 'Habitat et mode de vie',
      options: [
        'Convient à la vie en appartement',
        'Ne convient pas à la vie en appartement',
        'Convient à la vie en maison',
        'Ne convient pas à la vie en maison',
        'Hypoallergénique',
      ],
    },
    {
      title: 'Vie sociale',
      options: [
        'Entente avec les enfants',
        'Ne s’entend pas avec les enfants',
        'Entente avec les chiens',
        'Ne s’entend pas avec les chiens',
        'Entente avec les chats',
        'Ne s’entend pas avec les chats',
        'Entente avec les autres animaux',
        'Ne s’entend pas avec les autres animaux',
      ],
    },
    {
      title: 'Traits de caractère',
      options: ['Joueur', 'Calin', 'Craintif', 'Peureux', 'Agressif', 'Timide', 'Bruyant', 'Propre'],
    },
  ]
  const plainDescription = getPlainTextFromHtml(animal.description)
  const shouldTrimDescription = plainDescription.length > DESCRIPTION_PREVIEW_LENGTH
  const descriptionPreview = shouldTrimDescription
    ? `${plainDescription.substring(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}...`
    : plainDescription || '...'

  const overviewChips = [
    {
      key: 'hairLength',
      value: animal.hairLength ? getReadableAnimalHairLength(animal.hairLength) : undefined,
    },
    {
      key: 'hair',
      value: animal.hair || undefined,
    },
    {
      key: 'color',
      value: animal.color || undefined,
    },
  ].filter((chip): chip is { key: string; value: string } => Boolean(chip.value))

  return (
    <Card>
      <CardHeader
        title={`Présentation de ${animal.name}`}
        action={canEditAnimal && <EditorPromptWithFields title={`Présentation de ${animal.name}`} animal={animal} />}
        titleTypographyProps={{ sx: { fontSize: { xs: 15.5, sm: 16 }, fontWeight: 700, lineHeight: 1.2 } }}
        sx={{ pb: 0 }}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent sx={{ pt: 0 }}>
          <Box sx={{ mt: 2 }}>
            {overviewChips.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  mb: 1.5,
                }}
              >
                {overviewChips.map((chip) => (
                  <ReadOnlyChip key={chip.key} label="" value={chip.value} chipColor={colors.blueSky} />
                ))}
              </Box>
            )}

            <Typography
              variant={'body2'}
              color="textSecondary"
              sx={{
                lineHeight: '0.875rem',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                textDecoration: 'none',
                mb: 1,
              }}
            >
              Description
            </Typography>
            <Box
              sx={{
                color: '#203864',
                lineHeight: 1.55,
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.55,
                  color: '#203864',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {descriptionPreview}
              </Typography>
              {shouldTrimDescription && (
                <EditorPromptWithFields
                  title={getReadableAnimalFieldLabel('description')}
                  renderButton={(onClick: () => void): JSX.Element => (
                    <ButtonBase
                      onClick={onClick}
                      sx={{
                        display: 'inline-flex',
                        mt: 0.25,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#D39A12',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': {
                          color: '#B98507',
                        },
                      }}
                    >
                      voir plus
                    </ButtonBase>
                  )}
                  animal={animal}
                />
              )}
            </Box>
          </Box>

          <ControlledMultipleFreeSoloAutoCompleteWithCategories
            options={options}
            freeSolo={true}
            control={control}
            error={undefined}
            fieldName="characteristics"
            label="Caractéristiques"
            requiredRule=""
            sx={spacingItem}
          />
          <ControlledChipInput
            control={control}
            error={undefined}
            fieldName="identifyingMarks"
            label="Signes distinctifs"
            requiredRule=""
            helperText={`Entrez un signe distinctif puis appuyez sur "entrée" pour l'ajouter à la liste`}
            sx={spacingItem}
          />
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <AnimalCardSaveButton isLoading={editAnimalMutation.isLoading} disabled={!isDirty} />
        </CardActions>
      </form>
    </Card>
  )
}
